import type { AgentUIMessage } from "@/lib/ai/types";
import type {
  MessageExecutionSnapshot,
  MessageRenderGroup,
  MessageRenderModel,
  MessageViewPhase,
  ToolRenderPlan,
} from "./render-model";

const STANDALONE_TOOL_COMPLETION_STATES = new Set([
  "output-available",
  "output-denied",
]);
const TOOL_TERMINAL_STATES = new Set([
  ...STANDALONE_TOOL_COMPLETION_STATES,
  "output-error",
]);

/** streaming 中と confirmation 待ちを、最終回答前の継続中フェーズとして扱う。 */
export function isMessageViewPhaseOngoing(phase: MessageViewPhase): boolean {
  return phase === "live" || phase === "awaiting-user";
}

/** streaming 中に利用者へ現在の実行状況を残すべき Tool があるか判定する。 */
export function hasPendingToolExecution(
  toolPlans: ReadonlyMap<number, ToolRenderPlan>,
): boolean {
  return [...toolPlans.values()].some(
    (plan) => !TOOL_TERMINAL_STATES.has(plan.part.state),
  );
}

/**
 * standalone tool の前文と、tool 完了後に始まった最終回答を区別する。
 *
 * standalone表示を持つtoolが成功または拒否で完了するまで、その前のtextは
 * 前文として扱う。失敗時も詳細を確認できるよう最終回答開始の根拠にはしない。
 */
export function hasFinalResponseStarted(
  groups: MessageRenderGroup[],
  toolPlans: ReadonlyMap<number, ToolRenderPlan>,
): boolean {
  let latestPendingStandaloneToolIndex = -1;
  for (const [partIndex, plan] of toolPlans) {
    if (
      plan.placement !== "activity" &&
      !STANDALONE_TOOL_COMPLETION_STATES.has(plan.part.state)
    ) {
      latestPendingStandaloneToolIndex = Math.max(
        latestPendingStandaloneToolIndex,
        partIndex,
      );
    }
  }

  return groups.some((group) => {
    if (group.kind === "answer") {
      return group.parts.some(
        ({ part, partIndex }) =>
          partIndex > latestPendingStandaloneToolIndex &&
          part.text.trim().length > 0,
      );
    }
    return (
      group.kind === "standalone" &&
      group.partIndex > latestPendingStandaloneToolIndex &&
      group.part.state === "output-available"
    );
  });
}

/** Message の実行状態と表示内容から、表示フェーズを一意に決める。 */
export function deriveMessageViewPhase(
  message: AgentUIMessage,
  groups: MessageRenderGroup[],
  snapshot: MessageExecutionSnapshot,
  toolPlans: ReadonlyMap<number, ToolRenderPlan>,
): Pick<MessageRenderModel, "phase" | "incompleteCause"> {
  if (
    message.role === "assistant" &&
    snapshot.isLatestMessage &&
    (snapshot.chatStatus === "submitted" || snapshot.chatStatus === "streaming")
  ) {
    return { phase: "live" };
  }

  const isAwaitingUser = [...toolPlans.values()].some(
    (plan) =>
      plan.renderer === "confirmation" &&
      ["input-available", "approval-requested"].includes(plan.part.state),
  );
  if (isAwaitingUser) {
    return { phase: "awaiting-user" };
  }

  if (message.role === "assistant" && snapshot.completionCause) {
    return {
      phase: "incomplete",
      incompleteCause: snapshot.completionCause,
    };
  }

  const finalResponseStarted = hasFinalResponseStarted(groups, toolPlans);
  // output-error は失敗詳細を展開するため incomplete のままにし、それ以外の完了・拒否だけを完了扱いにする。
  const hasOnlyCompletedOrDeniedToolResults =
    toolPlans.size > 0 &&
    [...toolPlans.values()].every((plan) =>
      ["output-available", "output-denied"].includes(plan.part.state),
    );
  if (message.role === "assistant" && !finalResponseStarted) {
    if (
      !hasOnlyCompletedOrDeniedToolResults &&
      (snapshot.chatStatus === "ready" || snapshot.chatStatus === "error")
    ) {
      return { phase: "incomplete", incompleteCause: "unknown" };
    }
  }

  return { phase: "done" };
}
